import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon, ArchiveIcon } from 'lucide-react';
import type { AddOnRecord } from '../services/smr-admin';

interface Props {
    data: AddOnRecord[];
    isLoading?: boolean;
    onRetire?: (id: string) => void;
}

export const AddOnDataTable: React.FC<Props> = ({ data, isLoading = false, onRetire }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading optional extras..." />;
    }

    const columns: ColumnDef<AddOnRecord>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'category',
            header: 'Category',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? '—'}</span>,
        },
        {
            accessorKey: 'price',
            header: 'Net Price',
            cell: (info) => <span className="text-sm font-mono">£{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'gross_price',
            header: 'Gross Price',
            cell: (info) => <span className="text-sm font-mono">£{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'retired_at',
            header: 'Status',
            cell: (info) => {
                const retiredAt = info.getValue() as string | null;
                return retiredAt
                    ? <Badge variant="error">Retired</Badge>
                    : <Badge variant="success">Active</Badge>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View optional extra">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/addons/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon.edit' }}>
                        <Tooltip content="Edit optional extra">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/addons/${info.row.original.id}/edit`)}
                                color="secondary"
                                startIcon={<EditIcon size={18} />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon.delete' }}>
                        {!info.row.original.retired_at && onRetire && (
                            <Tooltip content="Retire optional extra">
                                <TextLink
                                    onClick={() => onRetire(info.row.original.id)}
                                    color="warning"
                                    startIcon={<ArchiveIcon size={18} />}
                                />
                            </Tooltip>
                        )}
                    </PermissionGuard>
                </div>
            ),
        },
    ];

    return <Table<AddOnRecord> data={data} columns={columns} enableSorting={false} />;
};
